import { inject, Injectable } from "@angular/core";
import { Feature, Fill, OverlayManager, Stroke, Style, VectorLayer, WKT } from "@vision/core-map-api";
import VectorLayerStyles from "@vision/core-map-api/src/utils/VectorLayerStyles";
import { FeatureEvents } from "@vision/core-map-api/src/constants/CoreMapConstants";
import { Subject } from "rxjs";
import { MapService } from "../../../viewer-components/services/internal-services/map.service";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { DetectionObject, DetectionResult } from "../../model/detection/detection.models";
import { MaskVectorizerService } from "../detection/mask-vectorizer.service";

export interface DetectionFeatureClick {
  objectName: string;
  detectionIndex: number;
}

const BBOX_PREFIX = "custom-elements-bbox-";
const MASK_PREFIX = "custom-elements-mask-";
const AREA_LAYER_ID = "custom-elements-area-layer";
const DEBUG_MERGED_LAYER_ID = "custom-elements-debug-merged-layer";
const DEBUG_TILES_LAYER_ID = "custom-elements-debug-tiles-layer";

/** Converts "#56A7B7" + alpha → "rgba(86,167,183,alpha)". */
function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace(/^#/, "");
  if (cleaned.length !== 6) return hex;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Manages the vector layers for Custom Elements detection results on the map.
 *
 * One bbox layer + one mask layer is created per detection object.
 * Both `setDetections()` and visibility toggles are fully synchronous —
 * mask vectorisation happens upstream in `DetectionService` before this
 * service is called, so `det.maskRings` is already populated.
 */
@Injectable()
export class DetectionsLayerService {
  private readonly mapService = inject(MapService);

  /** Ids of the singleton (area / debug) layers that have been created at least once. */
  private readonly singletonLayerIds = new Set<string>();

  private readonly bboxLayerNames = new Set<string>();

  private readonly featureClickSubject = new Subject<DetectionFeatureClick>();

  readonly featureClick$ = this.featureClickSubject.asObservable();

  private readonly maskLayerNames = new Set<string>();

  setDetections(
    objects: readonly DetectionObject[],
    results: Readonly<Record<string, DetectionResult>>,
    visibility: Readonly<Record<string, boolean>>,
    bboxVisibility: Readonly<Record<string, boolean>>,
    maskVisibility: Readonly<Record<string, boolean>>,
  ): void {
    try {
      const currentNames = new Set(objects.map((o) => o.name));
      for (const name of [...this.bboxLayerNames]) {
        if (!currentNames.has(name)) this.removeObjectLayers(name);
      }

      const wktReader = new WKT();

      for (const obj of objects) {
        const bboxLayer = this.ensureNamedLayer(BBOX_PREFIX, obj.name, this.bboxLayerNames);
        const maskLayer = this.ensureNamedLayer(MASK_PREFIX, obj.name, this.maskLayerNames);

        for (const f of bboxLayer.getFeatures() ?? []) bboxLayer.removeFeature(f);
        for (const f of maskLayer.getFeatures() ?? []) maskLayer.removeFeature(f);

        bboxLayer.setVisible(visibility[obj.name] !== false && bboxVisibility[obj.name] !== false);
        maskLayer.setVisible(visibility[obj.name] !== false && maskVisibility[obj.name] !== false);

        const objResult = results[obj.name];
        if (!objResult?.detections?.length) continue;

        const bboxStyle = this.createBboxStyle(obj.color);
        const maskStyle = this.createMaskStyle(obj.color);

        for (let i = 0; i < objResult.detections.length; i++) {
          const det = objResult.detections[i];
          if (!det.bboxWkt) continue;

          // ── Bbox ─────────────────────────────────────────────────────────
          try {
            const geom = wktReader.readGeometry(det.bboxWkt);
            const bboxFeature = new Feature(geom);
            bboxFeature.setStyle(bboxStyle);
            bboxFeature.set("objectName", obj.name);
            bboxFeature.set("detectionIndex", i);
            bboxFeature.addEventListener(FeatureEvents.FEATURE_CLICK, () => {
              this.featureClickSubject.next({ objectName: obj.name, detectionIndex: i });
            });
            bboxLayer.addFeature(bboxFeature);
          } catch (err) {
            console.error("DetectionsLayerService: failed to parse bbox WKT:", err, det.bboxWkt);
          }

          // ── Mask (synchronous — rings pre-computed by DetectionService) ──
          if (det.maskRings) {
            const ext = GeoService.wktPolygonToBbox(det.bboxWkt, false) as [number, number, number, number] | undefined;
            if (ext && ext.length >= 4) {
              const polygons = MaskVectorizerService.groupRingsIntoPolygons(det.maskRings.rings);
              for (const poly of polygons) {
                const wkt = MaskVectorizerService.maskPolygonToWkt(poly, det.maskRings.w, det.maskRings.h, ext);
                if (!wkt) continue;
                try {
                  const geom = wktReader.readGeometry(wkt);
                  const maskFeature = new Feature(geom);
                  maskFeature.setStyle(maskStyle);
                  maskFeature.addEventListener(FeatureEvents.FEATURE_CLICK, () => {
                    this.featureClickSubject.next({ objectName: obj.name, detectionIndex: i });
                  });
                  maskLayer.addFeature(maskFeature);
                } catch (err) {
                  console.error("DetectionsLayerService: failed to add mask polygon:", err);
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("DetectionsLayerService: setDetections failed:", error);
    }
  }

  /** Toggle bbox layer visibility for a single object — no feature rebuild. */
  updateBboxVisibility(name: string, on: boolean): void {
    try {
      const layer = this.getOverlayManager().getLayerById(BBOX_PREFIX + name) as VectorLayer | null;
      layer?.setVisible(on);
    } catch {
      /* layer may not exist yet */
    }
  }

  /** Toggle mask layer visibility for a single object — no feature rebuild. */
  updateMaskVisibility(name: string, on: boolean): void {
    try {
      const layer = this.getOverlayManager().getLayerById(MASK_PREFIX + name) as VectorLayer | null;
      layer?.setVisible(on);
    } catch {
      /* layer may not exist yet */
    }
  }

  clearDetections(): void {
    for (const name of [...this.bboxLayerNames]) {
      this.removeObjectLayers(name);
    }
    this.clearDebugMergedFragments();
  }

  setArea(wkt: string | undefined | null): void {
    if (!wkt) {
      this.clearArea();
      return;
    }
    try {
      const layer = this.ensureSingletonLayer(AREA_LAYER_ID);
      if (!layer) return;
      for (const f of layer.getFeatures() ?? []) layer.removeFeature(f);
      const feature = new Feature(new WKT().readGeometry(wkt));
      feature.setStyle(this.areaStyleFn);
      layer.addFeature(feature);
    } catch (error) {
      console.error("DetectionsLayerService: setArea failed:", error);
    }
  }

  clearArea(): void {
    this.clearSingletonLayer(AREA_LAYER_ID, "clearArea");
  }

  /**
   * Debug overlay: when `visible` is true, draws the pre-merge bbox of each
   * fragment that was fused into a merged detection, using a dashed magenta
   * stroke so users can see which fragments the algorithm combined. When
   * `visible` is false the debug layer is cleared but kept alive for cheap
   * subsequent toggles.
   */
  setDebugMergedFragments(
    objects: readonly DetectionObject[],
    results: Readonly<Record<string, DetectionResult>>,
    visible: boolean,
  ): void {
    const wkts = objects.flatMap((obj) => {
      const group = results[obj.name];
      if (!group?.detections?.length) return [];
      return group.detections.flatMap((det) => (det.mergedFrom && det.mergedFrom.length >= 2 ? det.mergedFrom : []));
    });
    this.renderDebugOverlay(
      DEBUG_MERGED_LAYER_ID,
      visible,
      wkts,
      this.createDebugFragmentStyle(),
      "debug fragment",
      "setDebugMergedFragments",
    );
  }

  clearDebugMergedFragments(): void {
    this.clearSingletonLayer(DEBUG_MERGED_LAYER_ID, "clearDebugMergedFragments");
  }

  /**
   * Debug overlay: when `visible` is true, draws each tile polygon the current
   * detection area was split into, using a dashed cyan stroke — a different
   * color from the magenta merged-fragments overlay so both can be told apart
   * on screen. When `visible` is false the debug layer is cleared but kept
   * alive for cheap subsequent toggles.
   */
  setDebugTiles(tileWkts: readonly string[], visible: boolean): void {
    this.renderDebugOverlay(
      DEBUG_TILES_LAYER_ID,
      visible,
      tileWkts,
      this.createDebugTileStyle(),
      "debug tile",
      "setDebugTiles",
    );
  }

  clearDebugTiles(): void {
    this.clearSingletonLayer(DEBUG_TILES_LAYER_ID, "clearDebugTiles");
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /** Creates (once) the per-object layer `prefix + name`, tracked in `tracker`, and returns it. */
  private ensureNamedLayer(prefix: string, name: string, tracker: Set<string>): VectorLayer {
    const id = prefix + name;
    if (!tracker.has(name) || !this.tryGetLayer(id)) {
      this.getOverlayManager().addLayer(new VectorLayer({ id, features: [] }));
      tracker.add(name);
    }
    return this.getOverlayManager().getLayerById(id) as VectorLayer;
  }

  private removeObjectLayers(name: string): void {
    this.tryRemoveLayer(BBOX_PREFIX + name);
    this.tryRemoveLayer(MASK_PREFIX + name);
    this.bboxLayerNames.delete(name);
    this.maskLayerNames.delete(name);
  }

  private tryGetLayer(id: string): VectorLayer | null {
    try {
      return this.getOverlayManager().getLayerById(id) as VectorLayer;
    } catch {
      return null;
    }
  }

  private tryRemoveLayer(id: string): void {
    try {
      const layer = this.tryGetLayer(id);
      if (layer) this.mapService.removeLayer(layer);
    } catch {
      /* layer may already be gone */
    }
  }

  /** Creates (once) the singleton layer `id` and returns it. Used for the area/debug overlays. */
  private ensureSingletonLayer(id: string): VectorLayer | null {
    if (!this.singletonLayerIds.has(id) || !this.tryGetLayer(id)) {
      this.getOverlayManager().addLayer(new VectorLayer({ id, features: [] }));
      this.singletonLayerIds.add(id);
    }
    return this.tryGetLayer(id);
  }

  /**
   * Clears a singleton layer's features. Bails out without touching the overlay
   * manager if that layer was never created — cheap no-op for the common case of
   * clearing an overlay that was never shown.
   */
  private clearSingletonLayer(id: string, errorContext: string): void {
    if (!this.singletonLayerIds.has(id)) return;
    try {
      const layer = this.tryGetLayer(id);
      if (layer) for (const f of layer.getFeatures() ?? []) layer.removeFeature(f);
    } catch (error) {
      console.error(`DetectionsLayerService: ${errorContext} failed:`, error);
    }
  }

  /**
   * Shared implementation for the debug overlays ({@link setDebugMergedFragments},
   * {@link setDebugTiles}): ensures the layer, clears previous features, toggles
   * visibility, then — only when visible — re-renders one feature per WKT.
   */
  private renderDebugOverlay(
    layerId: string,
    visible: boolean,
    wkts: readonly string[],
    style: Style,
    featureLabel: string,
    errorContext: string,
  ): void {
    try {
      const layer = this.ensureSingletonLayer(layerId);
      if (!layer) return;
      for (const f of layer.getFeatures() ?? []) layer.removeFeature(f);
      layer.setVisible(visible);
      if (!visible) return;

      const wktReader = new WKT();
      for (const wkt of wkts) {
        try {
          const feat = new Feature(wktReader.readGeometry(wkt));
          feat.setStyle(style);
          layer.addFeature(feat);
        } catch (err) {
          console.error(`DetectionsLayerService: failed to add ${featureLabel}:`, err);
        }
      }
    } catch (error) {
      console.error(`DetectionsLayerService: ${errorContext} failed:`, error);
    }
  }

  private getOverlayManager(): OverlayManager {
    return this.mapService.getOverlayManager();
  }

  private createBboxStyle(hex: string): Style {
    return new Style({
      stroke: new Stroke({ color: hex, width: 2 }),
      // fill: new Fill({ color: hexToRgba(hex, 0.08) }),
    });
  }

  private createMaskStyle(hex: string): Style {
    return new Style({
      fill: new Fill({ color: hexToRgba(hex, 0.35) }),
    });
  }

  private createDebugFragmentStyle(): Style {
    return new Style({
      stroke: new Stroke({ color: "#FF00FF", width: 1.5, lineDash: [4, 4] }),
      fill: new Fill({ color: "rgba(255,0,255,0.06)" }),
    });
  }

  private createDebugTileStyle(): Style {
    return new Style({
      stroke: new Stroke({ color: "#00E5FF", width: 1.5, lineDash: [4, 4] }),
      fill: new Fill({ color: "rgba(0,229,255,0.06)" }),
    });
  }

  private readonly areaStyleFn = VectorLayerStyles.defaultStyleSelectionLayer;
}
